import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../../app.config';
import {
    AuthUser,
    EffectiveUserPermission,
    SecurityScope,
} from '../../../auth/auth.model';
import { AuthService } from '../../../auth/auth.service';
import { PeopleOfInterestUserService } from '../../../user/people-of-interest.user.service';
import { PersonOfInterest } from '../models/coaching.global.model';
import { OaPerson } from '../models/oa.model';

@Injectable()
export class PersonOfInterestService {
    private readonly _favourites: BehaviorSubject<PersonOfInterest[]> =
        new BehaviorSubject<PersonOfInterest[]>(null);

    private readonly _favouriteView: BehaviorSubject<PersonOfInterest> =
        new BehaviorSubject<PersonOfInterest>(null);

    private readonly _storedPersonOfInterest: BehaviorSubject<PersonOfInterest> =
        new BehaviorSubject<PersonOfInterest>(null);

    public permissions: EffectiveUserPermission[];

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _authService: AuthService,
        private readonly _peopleOfInterestUserService: PeopleOfInterestUserService,
    ) {
        this.permissions = this._authService.user.permissions;
    }

    get favourites(): Observable<PersonOfInterest[]> {
        return this._favourites.asObservable();
    }

    get favouriteView(): Observable<PersonOfInterest> {
        return this._favouriteView.asObservable();
    }

    get user(): AuthUser {
        return this._authService.user;
    }

    getFavouriteView(): PersonOfInterest {
        return this._favouriteView.getValue();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetWidget() {
        this._favourites.next(null);
        this._favouriteView.next(null);
    }

    getCurrentViewId(): number {
        if (this._favouriteView.getValue()) {
            return this._favouriteView.getValue().userId;
        }
        return null;
    }

    retrieveFavourites(): Subscription {
        // TODO revise
        return of(null)
            .pipe(
                this._peopleOfInterestUserService.list(
                    () => SecurityScope.COACHING,
                ),
                tap((data) => this._mapFavouritesResponse(data)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private _mapFavouritesResponse(data) {
        const ppl = data;
        const n = [];

        if (!this.user) {
            console.error('User Information is unavailable');
            return false;
        }

        ppl.forEach((s: OaPerson) => {
            const x: any = s;
            x.value = s.userId.toString();
            x.label = `${s.firstName} ${s.lastName}`;
            n.push(x);
        });
        this._favourites.next(n);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addOrRemoveMemberToFavourites(userID: number) {
        const x = this._favourites.getValue().map((view) => {
            if (view.value.toString() === userID.toString()) {
                view.favorite = !view.favorite;
            }
            return view;
        });
        this._favourites.next(x);
    }

    addOrRemoveMemberViewList(
        userId: number,
        add: boolean,
    ): Observable<boolean> {
        let x: Observable<boolean> = of(null);

        if (add) {
            x = x.pipe(this._peopleOfInterestUserService.create(() => userId));
        } else {
            x = x.pipe(this._peopleOfInterestUserService.delete(() => userId));
        }

        return x.pipe(debounceTime(1000));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setDefaultFavourite() {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const user = {
            userId: this.user.id,
            value: this.user.id.toString(),
            firstName: this.user.firstName,
            lastName: this.user.lastName,
            isAvailable: true,
        } as PersonOfInterest;
        this.setFavourite(user);
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        return user as PersonOfInterest;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setFavourite(e: any, bypassFavouritesList?: boolean) {
        if (!e) console.error('Please select a Favourite.');

        if (!bypassFavouritesList) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            this._favouriteView.next(<PersonOfInterest>{
                ...e,
            });
        } else {
            const pois = this._favourites.getValue();

            if (!pois) return;

            let personid = null;

            if (e.userId != null) {
                personid = e.userId;
            } else if (e.systemUserId != null) {
                personid = e.systemUserId;
            } else if (e.value != null) {
                personid = e.value;
            }

            const poi = pois.find((f) => f.userId === personid);
            this._favouriteView.next(poi);
        }
    }

    setStoredFavourite(): PersonOfInterest {
        const newPerson: PersonOfInterest =
            this._storedPersonOfInterest.getValue();
        this._favouriteView.next(newPerson);
        this.setFavourite(newPerson);
        return newPerson;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    swapAndStoreFavourite(newperson: PersonOfInterest) {
        this._storedPersonOfInterest.next(this._favouriteView.getValue());
        this.setFavourite(newperson);
    }
}
